import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PerFlexRow from '../../components/custom_components/FlexRow'
import PerButton from '../../components/custom_components/Button'
import PerText from '../../components/custom_components/Text'
import { EmployeeTagType, RankInput } from '../../types'
import { useCultivatingTalentsStore } from './CultivatingTalentsStore'
import shallow from 'zustand/shallow'
import { confirmRankingSeason, saveTalentsSeasonRanks } from '../../graphql/talents-season/talentsSeasonMutations'
import { PerModal } from '../../components/custom_components/Modal'
import { showNotification } from '../../components/custom_components/Notification/notification'

export const TalentsSeasonConfirmationActions: FC = () => {
  const [showModal, toggleModal] = useState<boolean>(false)
  const [isProcessing, toggleProcessing] = useState<boolean>(false)

  const { t } = useTranslation()
  const [employees, rankedEmployees, setRankedEmployees, talentsSeasonSettings, rankingEditingInProgress, toggleRankingEditingInProgress] =
    useCultivatingTalentsStore(
      (state) => [
        state.employees,
        state.rankedEmployees,
        state.setRankedEmployees,
        state.talentsSeasonSettings,
        state.rankingEditingInProgress,
        state.toggleRankingEditingInProgress,
      ],
      shallow,
    )

  if (!talentsSeasonSettings) {
    return null
  }
  const { companyId, country, year, talentsSeasonId, topPercentage, lowPercentage } = talentsSeasonSettings

  const topTagCount = employees.filter((o) => o.tags.some((tag) => tag.type === EmployeeTagType.Top)).length
  const lowTagCount = employees.filter((o) =>
    o.tags.some((tag) => tag.type === EmployeeTagType.Low || tag.type === EmployeeTagType.EntryLevelLow),
  ).length

  const canConfirmForLowTags = lowTagCount >= Math.round(employees.length * (lowPercentage / 100))
  const canConfirmForTopTags = topTagCount <= Math.round(employees.length * (topPercentage / 100))

  const canConfirmTags = canConfirmForTopTags && canConfirmForLowTags

  const atLeastOneHoleInRanking = rankedEmployees.some((employee, index) => employee.rank !== index + 1)

  const handleSave = async () => {
    toggleProcessing(true)
    const newlyRankedEmployees = [...rankedEmployees]
    const ranks: RankInput[] = newlyRankedEmployees.map((e) => ({
      employeeId: e.employee.id,
      rank: e.rank,
      assessable: e.assessable,
    }))
    const res = await saveTalentsSeasonRanks(talentsSeasonId, ranks, companyId, country, year)
    if (!!res.errors && !!res.errors.length) {
      showNotification({
        type: 'error',
        message: t('rankingView.notifications.save.error.title'),
        description: t('rankingView.notifications.save.error.description'),
      })
    } else {
      showNotification({
        type: 'success',
        message: t('rankingView.notifications.save.success.title'),
        description: t('rankingView.notifications.save.success.description'),
      })
    }
    toggleProcessing(false)
    toggleRankingEditingInProgress(false)
  }

  const handleCancel = () => {
    setRankedEmployees(employees) // populating back the original un-ranked data
    toggleRankingEditingInProgress(false)
  }

  const handleSubmit = async () => {
    toggleProcessing(true)
    const { errors } = await confirmRankingSeason(companyId, country, year, talentsSeasonId)
    if (!!errors && !!errors.length) {
      showNotification({
        type: 'error',
        message: t('rankingView.notifications.confirm.error.title'),
        description: t('rankingView.notifications.confirm.error.description'),
      })
      toggleProcessing(false)
    } else {
      showNotification({
        type: 'success',
        message: t('rankingView.notifications.confirm.success.title'),
        description: t('rankingView.notifications.confirm.success.description'),
      })
      toggleModal(false)
    }
  }

  return (
    <>
      <PerFlexRow align="middle" justify={!rankingEditingInProgress ? 'end' : 'space-between'} mt="xs">
        {rankingEditingInProgress ? (
          <>
            <PerButton
              text={t('global.button.cancel')}
              color="secondary"
              before="close_stroke"
              variant="pastel"
              type="button"
              onClick={handleCancel}
            />
            <PerButton
              text={t('global.button.save')}
              color="success"
              before="check_stroke"
              type="button"
              onClick={handleSave}
              isProcessing={{ active: isProcessing }}
            />
          </>
        ) : (
          <PerButton
            text={t('global.button.confirm')}
            color="success"
            before="check_stroke"
            type="button"
            disabled={!canConfirmTags}
            isProcessing={{ active: isProcessing }}
            tooltip={
              !canConfirmForLowTags && !canConfirmForTopTags
                ? t('rankingView.messages.assignTagsByPercentage')
                : !canConfirmForLowTags
                ? t('rankingView.messages.assignLowTagsByPercentage')
                : !canConfirmForTopTags
                ? t('rankingView.messages.assignTopTagsByPercentage')
                : null
            }
            onClick={() => toggleModal(true)}
            data-testid="ttc-modal-open"
          />
        )}
      </PerFlexRow>

      <PerModal
        size="large"
        title={{
          heading: t('rankingView.modalRankingAndTagsConfirmation.header'),
        }}
        centered
        visible={showModal}
        closable={false}
        destroyOnClose={true}
        onCancel={() => toggleModal(false)}
        footer={{
          cancelBtn: {
            onClick: () => toggleModal(false),
          },
          customBtns: [
            {
              text: t('global.button.confirm'),
              onClick: () => {
                handleSubmit()
              },
              color: 'success',
              icon: 'check_stroke',
              isProcessing: { active: isProcessing },
            },
          ],
        }}
      >
        <PerText mb="m">{t('rankingView.modalRankingAndTagsConfirmation.description')}</PerText>
        {atLeastOneHoleInRanking && <PerText color="secondary">{t('rankingView.modalRankingAndTagsConfirmation.description2')}</PerText>}
      </PerModal>
    </>
  )
}
